import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { isValidEmail } from '../utils/inputValidation';
import { EditTransactionModal, DeleteConfirmPopup } from '../components/TransactionModals';
import { ArrowUpRight, ArrowDownLeft, Edit2, Trash2, FileText, Send, X, Loader2 } from 'lucide-react';
import Pagination from './Pagination';
import { useToast } from './Toast';
import { printInvoice } from '../utils/invoiceGenerator';

const FILTERS = [
  { value: '',            label: 'Tout',        color: '#0f172a', bg: '#f1f5f9' },
  { value: 'Prestation',  label: 'Prestations',  color: '#2563eb', bg: '#eff6ff' },
  { value: 'Vente',       label: 'Produits',     color: '#059669', bg: '#ecfdf5' },
  { value: 'Depense',     label: 'Dépenses',     color: '#dc2626', bg: '#fef2f2' },
];

const isIncome = (type) => ['Vente', 'Prestation'].includes(type);

const AdminTransactions = ({ salonDevise, salonTimezone, employees = [], rawDevise, salon }) => {
  const showToast = useToast();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filter, setFilter]             = useState('');
  const [page, setPage]                 = useState(1);
  const [pagination, setPagination]     = useState(null);
  const [selectedTx, setSelectedTx]     = useState(null);
  const [txToDelete, setTxToDelete]     = useState(null);
  const [sendTx, setSendTx]             = useState(null);
  const [sendEmail, setSendEmail]       = useState('');
  const [sending, setSending]           = useState(false);

  const fetchTransactions = async (currentPage = page) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/transactions?type=${filter}&page=${currentPage}&limit=20`);
      setTransactions(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Erreur transactions', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setPage(1); fetchTransactions(1); }, [filter]); // eslint-disable-line
  useEffect(() => { fetchTransactions(page); }, [page]); // eslint-disable-line

  const handleSendInvoice = async () => {
    if (!sendEmail || !sendTx || !isValidEmail(sendEmail)) return;
    setSending(true);
    try {
      await api.post(`/admin/transactions/${sendTx._id}/send-invoice`, { email: sendEmail });
      showToast(`Facture envoyée à ${sendEmail} !`);
      setSendTx(null);
      setSendEmail('');
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors de l\'envoi', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      await api.put(`/admin/transactions/${id}`, data);
      showToast('Transaction corrigée');
      setSelectedTx(null);
      fetchTransactions(page);
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors de la modification', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/transactions/${txToDelete}`);
      showToast('Transaction supprimée');
      setTxToDelete(null);
      fetchTransactions(page);
    } catch {
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short',
      ...(salonTimezone && { timeZone: salonTimezone }),
    });

  return (
    <div className="space-y-5 pb-6 animate-in fade-in duration-500">

      {/* ── Header + filtres ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black text-slate-800">Historique des flux</h2>
          <p className="text-sm text-slate-500 font-medium mt-0.5">
            Toutes les opérations financières
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map(f => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className="px-3.5 py-2 text-xs font-bold rounded-xl border-2 transition-all"
                style={{
                  background:  active ? f.color : '#fff',
                  borderColor: active ? f.color : '#e2e8f0',
                  color:       active ? '#fff'  : '#64748b',
                  boxShadow:   active ? `0 4px 14px ${f.color}30` : 'none',
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Desktop table ── */}
      <div className="hidden md:block">
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="space-y-px">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="h-[72px] animate-pulse bg-slate-50" />
              ))}
            </div>
          ) : transactions.length === 0 ? (
            <div className="py-20 text-center text-slate-400 font-semibold">
              Aucune transaction trouvée
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Date</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Opération</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Paiement</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Montant</th>
                  <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {transactions.map((t) => {
                  const income = isIncome(t.type);
                  return (
                    <tr key={t._id} className="hover:bg-slate-50/60 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0
                            ${income ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                            {income ? <ArrowUpRight size={15} /> : <ArrowDownLeft size={15} />}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-700">{fmtDate(t.date)}</p>
                            {t.isEdited && <p className="text-[10px] text-slate-400 italic">Modifié</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-slate-700 line-clamp-1">{t.description || 'Prestation'}</p>
                        <p className="text-[10px] text-indigo-500 font-black uppercase mt-0.5">
                          {t.employeeId ? `${t.employeeId.prenom} ${t.employeeId.nom}` : 'Admin'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-black uppercase">
                          {t.methodePaiement}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <p className={`text-sm font-black ${income ? 'text-emerald-600' : 'text-red-600'}`}>
                          {income ? '+' : '-'}{t.montant?.toLocaleString()}
                          <span className="text-[10px] ml-1 opacity-60">{salonDevise}</span>
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-1">
                          {t.invoiceNumber && (
                            <>
                              <button onClick={() => printInvoice({ transaction: t, salon })}
                                title={`Imprimer ${t.invoiceNumber}`}
                                className="p-2 hover:bg-amber-50 text-amber-600 rounded-lg transition-colors">
                                <FileText size={14} />
                              </button>
                              <button onClick={() => { setSendTx(t); setSendEmail(''); }}
                                title="Envoyer par email"
                                className="p-2 hover:bg-violet-50 text-violet-500 rounded-lg transition-colors">
                                <Send size={14} />
                              </button>
                            </>
                          )}
                          <button onClick={() => setSelectedTx(t)}
                            className="p-2 hover:bg-indigo-50 text-indigo-500 rounded-lg transition-colors">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => setTxToDelete(t._id)}
                            className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Mobile cards ── */}
      <div className="md:hidden space-y-2">
        {loading ? (
          <div className="space-y-2">
            {[1,2,3].map(i => (
              <div key={i} className="h-24 rounded-2xl bg-white animate-pulse border border-slate-100" />
            ))}
          </div>
        ) : transactions.length === 0 ? (
          <div className="py-16 text-center text-slate-400 font-semibold bg-white rounded-2xl border border-slate-100">
            Aucune transaction trouvée
          </div>
        ) : (
          transactions.map((t) => {
            const income = isIncome(t.type);
            return (
              <div key={t._id}
                className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                <div className="flex items-stretch">
                  <div className="w-1 flex-shrink-0" style={{ background: income ? '#10b981' : '#ef4444' }} />
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-slate-800 leading-snug line-clamp-2">
                          {t.description || 'Prestation'}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span className="text-[10px] text-indigo-500 font-black uppercase">
                            {t.employeeId ? `${t.employeeId.prenom} ${t.employeeId.nom}` : 'Admin'}
                          </span>
                          <span className="text-[10px] text-slate-400">{fmtDate(t.date)}</span>
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-md text-[10px] font-bold uppercase">
                            {t.methodePaiement}
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-base font-black ${income ? 'text-emerald-600' : 'text-red-600'}`}>
                          {income ? '+' : '-'}{t.montant?.toLocaleString()}
                        </p>
                        <p className="text-[10px] text-slate-400 font-bold">{salonDevise}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-slate-50">
                      {t.isEdited && <span className="text-[10px] text-slate-400 italic mr-auto">Modifié</span>}
                      {t.invoiceNumber && (
                        <>
                          <button onClick={() => printInvoice({ transaction: t, salon })}
                            className="p-2 hover:bg-amber-50 text-amber-600 rounded-lg transition-colors">
                            <FileText size={14} />
                          </button>
                          <button onClick={() => { setSendTx(t); setSendEmail(''); }}
                            className="p-2 hover:bg-violet-50 text-violet-500 rounded-lg transition-colors">
                            <Send size={14} />
                          </button>
                        </>
                      )}
                      <button onClick={() => setSelectedTx(t)}
                        className="p-2 hover:bg-indigo-50 text-indigo-500 rounded-lg transition-colors">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => setTxToDelete(t._id)}
                        className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Pagination pagination={pagination} onPageChange={setPage} />

      {selectedTx && (
        <EditTransactionModal
          transaction={selectedTx}
          currency={salonDevise}
          employees={employees}
          rawDevise={rawDevise}
          onClose={() => setSelectedTx(null)}
          onUpdate={handleUpdate}
        />
      )}
      {txToDelete && (
        <DeleteConfirmPopup
          onConfirm={handleDelete}
          onCancel={() => setTxToDelete(null)}
        />
      )}

      {/* ── Modal envoi facture par email ── */}
      {sendTx && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm p-8 relative">
            <button onClick={() => setSendTx(null)}
              className="absolute top-5 right-5 p-2 rounded-full hover:bg-slate-100 text-slate-400 transition-colors">
              <X size={18} />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-violet-100 flex items-center justify-center">
                <Send size={18} className="text-violet-600" />
              </div>
              <div>
                <h3 className="font-black text-slate-800 text-base leading-tight">Envoyer la facture</h3>
                <p className="text-xs text-slate-400 font-medium mt-0.5">{sendTx.invoiceNumber}</p>
              </div>
            </div>
            <div className="mb-5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                Email du client
              </label>
              <input
                type="email"
                value={sendEmail}
                onChange={e => setSendEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSendInvoice()}
                placeholder="client@email.com"
                autoFocus
                className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-medium text-slate-700 text-sm focus:ring-2 ring-violet-500/20 border border-transparent focus:border-violet-300"
              />
            </div>
            <button
              onClick={handleSendInvoice}
              disabled={!isValidEmail(sendEmail) || sending}
              className="w-full flex items-center justify-center gap-2 p-4 bg-slate-900 text-white rounded-2xl font-black text-sm disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              {sending
                ? <><Loader2 size={16} className="animate-spin" /> Envoi en cours…</>
                : <><Send size={16} /> Envoyer la facture</>
              }
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTransactions;
